import type {RefCallback, RefObject} from 'preact';
import {forwardRef} from 'preact/compat';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import {v4 as uuidv4} from 'uuid';

import {useAuthorizeState} from '~/foundation/Authorize/hooks';
import {useBugsnag} from '~/foundation/Bugsnag/hooks';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {useOpenTelemetry} from '~/foundation/OpenTelemetry/hooks';
import type {AuthorizeEventHandlers} from '~/hooks/useAuthorizeEventListener';
import {useAuthorizeEventListener} from '~/hooks/useAuthorizeEventListener';
import {useDispatchEvent} from '~/hooks/useDispatchEvent';
import {useLoadTimeout} from '~/hooks/useLoadTimeout';
import type {AuthorizeProps, SdkErrorCode} from '~/types/authorize';
import type {MessageEventData, PayEvents} from '~/types/event';
import {buildAuthorizeUrl} from '~/utils/authorize';
import {isoDocument} from '~/utils/document';
import {postMessage} from '~/utils/postMessage';

import {Modal} from '../Modal/Modal';

export interface AuthorizeIframeProps
  extends AuthorizeProps,
    AuthorizeEventHandlers {
  activator?: RefObject<HTMLElement>;
  anchorTo?: string;
  autoOpen?: boolean;
  keepModalOpen?: boolean;
  storefrontOrigin?: string;
}

export interface AuthorizeIframeElement {
  close: () => void;
  loaded: boolean;
  open: () => void;
  postMessage: (event: PayEvents) => void;
  reload: () => void;
  sessionDetected: boolean;
  source: Window | null;
  visible: boolean;
  waitForMessage: (
    messageType: MessageEventData['type'],
    signal?: AbortSignal,
  ) => Promise<MessageEventData>;
}

const SILENT_SDK_ERROR_CODES: SdkErrorCode[] = ['captcha_challenge'];

export const AuthorizeIframe = forwardRef<
  AuthorizeIframeElement,
  AuthorizeIframeProps
>(
  (
    {
      activator,
      analyticsContext = 'loginWithShop',
      analyticsTraceId,
      anchorTo,
      autoOpen,
      avoidPayAltDomain = false,
      avoidSdkSession = false,
      checkoutToken,
      checkoutVersion,
      clientId,
      codeChallenge,
      codeChallengeMethod,
      consentChallenge,
      disableSignUp = false,
      emailVerificationRequired = false,
      flow = 'default',
      flowVersion = 'unspecified',
      keepModalOpen = false,
      onComplete,
      onError,
      onLoaded,
      onModalVisibleChange,
      popUpFeatures,
      popUpName,
      proxy,
      redirectType,
      redirectUri,
      requireVerification,
      responseMode,
      responseType,
      scope,
      state: oAuthState,
      storefrontOrigin,
      transactionParams,
    },
    ref,
  ) => {
    const {trackPageImpression, trackShopPayModalStateChange} = useMonorail();
    const {leaveBreadcrumb, notify} = useBugsnag();
    const {recordCounter} = useOpenTelemetry();
    const {clearLoadTimeout, initLoadTimeout} = useLoadTimeout();
    const {dispatch, state} = useAuthorizeState();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const prevModalVisible = useRef(false);

    const dispatchEvent = useDispatchEvent();

    const callbackRef: RefCallback<HTMLIFrameElement> = (ref) => {
      /**
       * When the user restarts, the render logic changes for our iframe and ref is set to null.
       * We want to prevent from overriding the ref and setting it to null here because it breaks
       * the postMessage logic.
       */
      if (!ref) {
        return;
      }

      iframeRef.current = ref;
    };

    const handleShowModal = useCallback(() => {
      setModalVisible(true);
      trackShopPayModalStateChange({
        currentState: 'shown',
      });
    }, [trackShopPayModalStateChange]);

    const handleDismissModal = useCallback(() => {
      setModalVisible(false);

      // Focuses the activator after the modal closes.
      if (activator?.current && isRef(activator)) {
        activator.current.focus();
      }
      trackShopPayModalStateChange({currentState: 'hidden'});
    }, [activator, trackShopPayModalStateChange]);

    const postMessageCallback = useCallback(
      (event: PayEvents) =>
        postMessage({
          contentWindow: iframeRef?.current?.contentWindow,
          event,
        }),
      [],
    );

    useEffect(() => {
      function onClick() {
        handleShowModal();
      }

      const internalActivatorRef = activator;

      if (internalActivatorRef?.current && isRef(internalActivatorRef)) {
        internalActivatorRef.current.addEventListener('click', onClick);

        return () => {
          internalActivatorRef.current?.removeEventListener('click', onClick);
        };
      }
    }, [activator, handleShowModal]);

    useEffect(() => {
      if (!dispatch) {
        return;
      }

      if (analyticsContext === 'loginWithShopClassicCustomerAccounts') {
        dispatch({
          payload: flowVersion === 'sign_up' ? 'classic' : 'phoneAuth',
          type: 'setFlow',
        });
      }

      if (analyticsContext === 'loginWithShopPaymentRequest') {
        dispatch({
          payload: 'paymentRequest',
          type: 'setFlow',
        });
      }
    }, [analyticsContext, dispatch, flowVersion]);

    const authorizeUrl = useMemo(() => {
      return buildAuthorizeUrl({
        analyticsContext,
        analyticsTraceId: analyticsTraceId || uuidv4(),
        avoidPayAltDomain,
        avoidSdkSession,
        checkoutToken,
        checkoutVersion,
        consentChallenge,
        disableSignUp,
        emailVerificationRequired,
        flow,
        flowVersion,
        oauthParams: {
          clientId,
          codeChallenge,
          codeChallengeMethod,
          redirectType,
          redirectUri,
          responseMode,
          responseType,
          scope,
          state: oAuthState,
        },
        popupWindowParams: {
          popUpFeatures,
          popUpName,
        },
        proxy,
        requireVerification,
        transactionParams,
      });
    }, [
      analyticsContext,
      analyticsTraceId,
      avoidPayAltDomain,
      avoidSdkSession,
      checkoutToken,
      checkoutVersion,
      clientId,
      codeChallenge,
      codeChallengeMethod,
      consentChallenge,
      disableSignUp,
      emailVerificationRequired,
      flow,
      flowVersion,
      oAuthState,
      popUpFeatures,
      popUpName,
      proxy,
      redirectType,
      redirectUri,
      requireVerification,
      responseMode,
      responseType,
      scope,
      transactionParams,
    ]);

    const reloadIframe = useCallback(() => {
      // We cannot use iframeRef.current?.reload() due to cross-origin restrictions
      iframeRef.current?.setAttribute('src', '');
      iframeRef.current?.setAttribute('src', authorizeUrl);
    }, [authorizeUrl]);

    const {destroy, waitForMessage} = useAuthorizeEventListener({
      includeCore: proxy,
      onClose: handleDismissModal,
      onComplete: (completedEvent) => {
        if (!keepModalOpen) {
          handleDismissModal();
        }
        onComplete?.(completedEvent);
      },
      onEmailChangeRequested: () => {
        dispatch({type: 'restart'});
      },
      onError: (event) => {
        const {message, code} = event;
        if (SILENT_SDK_ERROR_CODES.includes(code)) {
          recordCounter('shop_js_handle_silent_error', {
            attributes: {
              errorCode: code,
            },
          });
        } else {
          notify(new Error(`Authorize Error: ${message} (${code}).`));
        }

        clearLoadTimeout();
        onError?.(event);
      },
      onLoaded: (event) => {
        if (analyticsContext === 'loginWithShop') {
          const authLevel = event?.authenticationLevelRequired || 'phone';

          dispatch({
            payload: authLevel === 'phone' ? 'phoneAuth' : 'emailAuth',
            type: 'setFlow',
          });
        }

        dispatch({payload: event, type: 'initialize'});
        setLoaded(true);
        onLoaded?.(event);
        clearLoadTimeout();
        trackShopPayModalStateChange({
          currentState: 'loaded',
        });

        /**
         * web-component compatibility since attributes on web components are not boolean based.
         * Check if the prop is defined as an attribute and that it is not set to false explicitly.
         */
        if (event.userFound && autoOpen !== undefined && autoOpen !== false) {
          setModalVisible(true);
        }
      },
      onPopUpOpened: (event) => {
        if (event.didOpen) {
          dispatch({payload: event, type: 'popup'});
        }
      },
      onResizeIframe: ({height}) => {
        if (iframeRef.current) {
          iframeRef.current.style.height = `${height}px`;
        }
      },
      onShopUserMatched: (event) => {
        dispatchEvent('shopusermatched');
        dispatch({payload: event, type: 'setUserMatched'});
      },
      onShopUserNotMatched: ({apiError}) => {
        dispatchEvent('shopusernotmatched', apiError && {apiError});
        dispatch({payload: {matched: false}, type: 'setUserMatched'});
      },
      source: iframeRef,
      storefrontOrigin,
    });

    useEffect(() => {
      return () => {
        if (iframeRef.current) {
          destroy();
        }
      };
    }, [destroy]);

    useEffect(() => {
      if (modalVisible) {
        postMessageCallback({
          type: 'sheetmodalopened',
        });
        dispatchEvent('modalopened');
        onModalVisibleChange?.(true);

        prevModalVisible.current = true;
      } else if (!modalVisible && prevModalVisible.current === true) {
        // Only runs when modal goes from opened to closed
        postMessageCallback({
          type: 'sheetmodalclosed',
        });
        dispatchEvent('modalclosed');
        onModalVisibleChange?.(false);

        // Remove the 1password custom element from the DOM after the sheet modal is closed.
        isoDocument.querySelector('com-1password-notification')?.remove();
      }
    }, [
      dispatchEvent,
      modalVisible,
      onModalVisibleChange,
      postMessageCallback,
    ]);

    useImperativeHandle(
      ref,
      () => {
        return {
          close: handleDismissModal,
          loaded,
          open: handleShowModal,
          postMessage: postMessageCallback,
          reload: reloadIframe,
          sessionDetected: state.sessionDetected,
          source: iframeRef.current?.contentWindow || null,
          visible: modalVisible,
          waitForMessage,
        };
      },
      [
        handleDismissModal,
        handleShowModal,
        loaded,
        modalVisible,
        postMessageCallback,
        reloadIframe,
        state.sessionDetected,
        waitForMessage,
      ],
    );

    useEffect(() => {
      initLoadTimeout();
      leaveBreadcrumb('Iframe url updated', {authorizeUrl}, 'state');
    }, [authorizeUrl, initLoadTimeout, leaveBreadcrumb]);

    useEffect(() => {
      if (modalVisible) {
        trackPageImpression({page: 'AUTHORIZE_MODAL'});
      }
    }, [modalVisible, trackPageImpression]);

    return (
      <Modal
        anchorTo={anchorTo}
        onDismiss={handleDismissModal}
        visible={modalVisible}
      >
        <iframe
          allow="publickey-credentials-get *"
          className="relative z-40 m-auto w-full border-none"
          ref={callbackRef}
          src={authorizeUrl}
          tabIndex={0}
          data-testid="authorize-iframe"
        />
      </Modal>
    );
  },
);

AuthorizeIframe.displayName = 'AuthorizeIframe';

function isRef(ref: RefObject<HTMLElement>): ref is RefObject<HTMLElement> {
  return Object.prototype.hasOwnProperty.call(ref, 'current');
}
